/**
 * Helper utilities for parsing and formatting user preferences
 *
 * The UI settings (display mode, dock position, and drawer position) are stored in a dedicated
 * "settings" field as JSON, separate from language and theme_mode fields.
 *
 * Settings field format:
 * {
 *   "displayMode": "card" | "dock" | "drawer",
 *   "dockPosition": "bottom" | "left" | "right",
 *   "drawerPosition": "bottom" | "left" | "right"
 * }
 */

export type DisplayMode = "card" | "dock" | "drawer";
export type DockPosition = "bottom" | "left" | "right";
export type DrawerPosition = "bottom" | "left" | "right";

export interface UISettings {
  displayMode: DisplayMode;
  dockPosition: DockPosition;
  drawerPosition: DrawerPosition;
  linkOrder?: string[];
}

/**
 * Default UI settings
 */
export const DEFAULT_UI_SETTINGS: UISettings = {
  displayMode: "dock",
  dockPosition: "right",
  drawerPosition: "bottom",
};

/**
 * Parse UI settings from the settings field
 * @param settingsField - The raw settings field from the database (JSON string)
 * @returns Parsed UI settings
 */
export const parseUISettings = (
  settingsField: string | undefined | null,
): UISettings => {
  // Handle empty/null settings field
  if (!settingsField || settingsField.trim() === "") {
    return { ...DEFAULT_UI_SETTINGS };
  }

  // Try to parse as JSON
  try {
    const parsed = JSON.parse(settingsField);

    // Validate the structure
    if (typeof parsed === "object" && parsed !== null) {
      let uiSettings: UISettings = { ...DEFAULT_UI_SETTINGS };

      // Parse display mode
      if (
        parsed.displayMode === "card" ||
        parsed.displayMode === "dock" ||
        parsed.displayMode === "drawer"
      ) {
        uiSettings.displayMode = parsed.displayMode;
      }

      // Parse dock position
      if (
        parsed.dockPosition === "bottom" ||
        parsed.dockPosition === "left" ||
        parsed.dockPosition === "right"
      ) {
        uiSettings.dockPosition = parsed.dockPosition;
      }

      // Parse drawer position
      if (
        parsed.drawerPosition === "bottom" ||
        parsed.drawerPosition === "left" ||
        parsed.drawerPosition === "right"
      ) {
        uiSettings.drawerPosition = parsed.drawerPosition;
      }

      // Parse link order
      if (Array.isArray(parsed.linkOrder)) {
        uiSettings.linkOrder = parsed.linkOrder.filter(
          (id: unknown) => typeof id === "string",
        );
      }

      return uiSettings;
    }
  } catch (e) {
    console.warn("Failed to parse UI settings, using defaults:", e);
  }

  // Return defaults if parsing failed
  return { ...DEFAULT_UI_SETTINGS };
};

/**
 * Format UI settings into JSON string for storage in the settings field
 * @param uiSettings - UI settings (display mode, dock position, and drawer position)
 * @returns Formatted settings field for database storage
 */
export const formatUISettings = (uiSettings: UISettings): string => {
  const data: Record<string, unknown> = {
    displayMode: uiSettings.displayMode || DEFAULT_UI_SETTINGS.displayMode,
    dockPosition: uiSettings.dockPosition || DEFAULT_UI_SETTINGS.dockPosition,
    drawerPosition:
      uiSettings.drawerPosition || DEFAULT_UI_SETTINGS.drawerPosition,
  };

  if (uiSettings.linkOrder && uiSettings.linkOrder.length > 0) {
    data.linkOrder = uiSettings.linkOrder;
  }

  return JSON.stringify(data);
};
